import { useEffect, useRef } from "react"
import { haptic } from "../lib/haptics"

const EDGE = 24
const START_SLOP = 10
const THRESHOLD = 96
const FLICK_VELOCITY = 0.6
const SIZE = 44

/**
 * Жест «назад» от левого края (как в daily-reports): контент стоит на месте, из-за края
 * выдвигается чёрный кружок. Выдвинулся наполовину — вибрация, можно отпускать.
 */
export function SwipeBack({ enabled, onBack }: { enabled: boolean; onBack: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    if (!enabled) return

    let track: {
      x0: number
      y0: number
      t0: number
      dx: number
      dragging: boolean
      armed: boolean
    } | null = null

    const render = (dx: number) => {
      const el = ref.current
      if (!el) return
      const shown = Math.min(dx / THRESHOLD, 1) * (SIZE / 2)
      el.style.transition = "none"
      el.style.transform = `translate(${Math.round(-SIZE + shown)}px, -50%)`
    }

    const hide = () => {
      const el = ref.current
      if (!el) return
      el.style.transition = "transform 0.2s ease-out"
      el.style.transform = `translate(${-SIZE}px, -50%)`
    }

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]
      // Открыт диалог/шторка — жест не наш.
      const modalOpen = !!document.querySelector('[role="dialog"][data-state="open"]')
      track =
        !modalOpen && t && e.touches.length === 1 && t.clientX <= EDGE
          ? { x0: t.clientX, y0: t.clientY, t0: e.timeStamp, dx: 0, dragging: false, armed: false }
          : null
    }

    const onMove = (e: TouchEvent) => {
      if (!track) return
      const t = e.touches[0]
      if (!t) return
      const dx = t.clientX - track.x0
      const dy = t.clientY - track.y0
      if (!track.dragging) {
        // Вертикаль — отдаём странице; вправо от края — гасим горизонтальный скролл таблиц сразу,
        // иначе браузер успеет его начать и отменить будет нельзя.
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 4) {
          track = null
          return
        }
        if (dx < -START_SLOP) {
          track = null
          return
        }
        if (dx > 0 && e.cancelable) e.preventDefault()
        if (dx > START_SLOP) track.dragging = true
        else return
      }
      if (e.cancelable) e.preventDefault()
      track.dx = Math.max(0, dx)
      const armed = track.dx >= THRESHOLD
      if (armed && !track.armed) haptic.impact("light")
      track.armed = armed
      render(track.dx)
    }

    const onEnd = (e: TouchEvent) => {
      if (!track?.dragging) {
        track = null
        return
      }
      const { dx, t0, armed } = track
      track = null
      const velocity = dx / Math.max(1, e.timeStamp - t0)
      hide()
      if (armed || velocity > FLICK_VELOCITY) onBackRef.current()
    }

    const capture = { capture: true } as const
    window.addEventListener("touchstart", onStart, { passive: true, capture: true })
    window.addEventListener("touchmove", onMove, { passive: false, capture: true })
    window.addEventListener("touchend", onEnd, capture)
    window.addEventListener("touchcancel", onEnd, capture)
    return () => {
      window.removeEventListener("touchstart", onStart, capture)
      window.removeEventListener("touchmove", onMove, capture)
      window.removeEventListener("touchend", onEnd, capture)
      window.removeEventListener("touchcancel", onEnd, capture)
      hide()
    }
  }, [enabled])

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed top-1/2 left-0 z-[60] flex size-11 items-center justify-end rounded-full bg-foreground pr-1 text-card"
      style={{ transform: `translate(${-SIZE}px, -50%)` }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 12H5" />
        <path d="M11 6l-6 6 6 6" />
      </svg>
    </div>
  )
}
